<template>
  <BaseDialog
    data-test-id="dialogSlotsConfirm"
    v-model="model"
    v-bind="$attrs"
    :max-width="500"
    :min-height="350"
    icon="mdi-calendar-multiple"
  >
    <v-container
      v-if="store.totalSlots > 0 || store.mode === ManageSlotsMode.Clear"
      class="ma-0 pa-0"
      fluid
    >
      <v-row>
        <v-col>
          {{ getDialogText() }}
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="store.mode === ManageSlotsMode.Create">
          {{ store.percentComplete }}%
          <v-progress-linear
            color="primary"
            height="10"
            v-model:model-value="store.percentComplete"
            :striped="store.status === ManageSlotsStatus.InProgress"
          ></v-progress-linear>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-else class="ma-0 pa-0" fluid>
      <v-row>
        <v-col>
          {{ getDialogNoSlotsText() }}
        </v-col>
      </v-row>
    </v-container>
    <template v-slot:actions>
      <ButtonSecondary
        v-if="store.status !== ManageSlotsStatus.Complete"
        @click="cancelEdit"
        :text="getCancelButtonText()"
      />
      <v-spacer />
      <ButtonPrimary
        v-if="
          store.status !== ManageSlotsStatus.Complete &&
          (store.totalSlots > 0 || store.mode === ManageSlotsMode.Clear)
        "
        @click="createOrClearOpenSlots"
        type="submit"
        :loading="
          store.status === ManageSlotsStatus.InProgress || eventsStore.workStatus.deleteMany
        "
        :text="getSaveButtonText()"
      />
      <ButtonPrimary
        v-if="store.status === ManageSlotsStatus.Complete"
        @click="closeDialog"
        type="submit"
        :text="t('buttons.done')"
      />
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { convertLocalDaysToUTC } from '@/composables/utils'
import { ManageSlotsMode } from '@/enums/ManageSlotsMode'
import { ManageSlotsStatus } from '@/enums/ManageSlotsStatus'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useCalendarStore } from '@/stores/ui/CalendarStore'
import { useSlotsStore } from '@/stores/ui/SlotsStore'
import { useI18n } from 'vue-i18n'

const calendarStore = useCalendarStore()
const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
const store = useSlotsStore()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const model = defineModel<boolean>({ default: false })

// REACTIVE VARIABLES

// COMPUTED PROPERTIES

// LIFECYCLE HOOKS

// FUNCTIONS
const cancelEdit = () => {
  if (store.status !== ManageSlotsStatus.InProgress) {
    store.showCreateEvents = false
  }
  store.status = ManageSlotsStatus.Paused
}

const closeDialog = () => {
  store.showCreateEvents = false
}

const createOrClearOpenSlots = async () => {
  switch (store.mode) {
    case ManageSlotsMode.Create: {
      await store.saveSlots()
      await store.loadOptions()
      break
    }
    case ManageSlotsMode.Clear: {
      const startDay = store.startDate.getDate()
      const startMonth = store.startDate.getMonth()
      const startYear = store.startDate.getFullYear()
      const startHours = store.startTime.hours
      const startMinutes = store.startTime.minutes
      const endDay = store.endDate.getDate()
      const endMonth = store.endDate.getMonth()
      const endYear = store.endDate.getFullYear()
      const endHours = store.endTime.hours
      const endMinutes = store.endTime.minutes
      const startDateValue = new Date(startYear, startMonth, startDay, startHours, startMinutes, 0)
      const endDateValue = new Date(endYear, endMonth, endDay, endHours, endMinutes, 0)

      // Adjust the daysSelected based on the local vs UTC day difference
      const daysSelected = convertLocalDaysToUTC(store.daysSelected, startDateValue)
      store.showCreateEvents = false
      await eventsStore.clearOpenSlots(
        startDateValue,
        endDateValue,
        daysSelected,
        store.eventCategory
      )
      await eventsStore.loadItems()
      break
    }
  }
}

const getDialogText = () => {
  if (store.mode === ManageSlotsMode.Clear) {
    const eventCategoryTitle = eventsStore.getCategoryTitle(store.eventCategory)
    return t('text.clearSlots', {
      eventCategoryTitle,
      startDate: store.startDate.toLocaleDateString(),
      startTime: store.startTime.toTimeString(calendarStore.twelveHourClock),
      endDate: store.endDate.toLocaleDateString(),
      endTime: store.endTime.toTimeString(calendarStore.twelveHourClock)
    })
  }
  let text = t('text.createSlots', {
    startDate: store.startDate.toLocaleDateString(),
    startTime: store.startTime.toTimeString(calendarStore.twelveHourClock),
    endDate: store.endDate.toLocaleDateString(),
    endTime: store.endTime.toTimeString(calendarStore.twelveHourClock),
    total: store.totalSlots
  })
  if (store.skippedSlots?.length > 0) {
    text += ` ${t('text.skipSlots', store.skippedSlots.length)}`
  }
  return text
}

const getDialogNoSlotsText = () => {
  return t('text.noSlots', {
    startDate: store.startDate.toLocaleDateString(),
    startTime: store.startTime.toTimeString(calendarStore.twelveHourClock),
    endDate: store.endDate.toLocaleDateString(),
    endTime: store.endTime.toTimeString(calendarStore.twelveHourClock)
  })
}

const getCancelButtonText = () => {
  if (store.totalSlots === 0 && store.mode === ManageSlotsMode.Create) {
    return t('buttons.close')
  }
  if (store.status === ManageSlotsStatus.InProgress) {
    return t('buttons.pause')
  }
  return t('buttons.cancel')
}

const getSaveButtonText = () => {
  if (store.status === ManageSlotsStatus.Paused) {
    return t('buttons.resume')
  }
  if (store.mode === ManageSlotsMode.Clear) {
    return t('buttons.clear')
  }
  return t('buttons.create')
}
</script>

<style lang="scss" scoped>
.dialog-text {
  margin-bottom: 0rem;
}
</style>
